// src/app/routeAccess.ts

export interface RouteAccessFlags {
  isUploadOnly: boolean;
  canIngest: boolean;
  canViewMedia: boolean;
}

/**
 * App-wide route access policy (pure). Consumed by useAppRouteGuard in the
 * AppShell. UX gating only — the backend stays the authority on every call.
 */
export class RouteAccess {
  /** Route roots that require media access (serviceRead or subcustomer pair). */
  static readonly MEDIA_PREFIXES = [
    '/videos',
    '/playlists',
    '/streams',
    '/players',
    '/feeds',
    '/apps',
  ] as const;

  private static isUnder(pathname: string, prefix: string): boolean {
    return pathname === prefix || pathname.startsWith(prefix + '/');
  }

  /** Returns the redirect target for a disallowed path, or null when allowed. */
  static getRedirect(pathname: string, flags: RouteAccessFlags): string | null {
    // Upload-only users live on /upload; /account stays reachable so users
    // with account-level perms keep them (AccountLayout guards per-page, and
    // its no-perm fallback to '/' re-enters this guard → /upload, no loop).
    if (flags.isUploadOnly) {
      const allowed = this.isUnder(pathname, '/upload') || this.isUnder(pathname, '/account');
      return allowed ? null : '/upload';
    }

    if (!flags.canIngest && this.isUnder(pathname, '/upload')) {
      return '/';
    }

    if (!flags.canViewMedia && this.MEDIA_PREFIXES.some((p) => this.isUnder(pathname, p))) {
      return '/account';
    }

    return null;
  }
}
